import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link, Success } from "@washingtonpost/wpds-assets";
import { Button, Icon, styled } from "@washingtonpost/wpds-ui-kit";
import "../index.css";
import { copyTextToClipboard } from "./utils";

const CopyButtonRoot = styled("div", {
  position: "relative",
  display: "inline-flex",
  flexShrink: 0
});

const StyledText = styled("span", {
  position: "absolute",
  left: "100%",
  top: "50%",
  marginLeft: "2px",
  transform: "translateY(-50%)",
  fontSize: "$075",
  padding: "4px 6px",
  color: "white",
  borderRadius: "5px",
  whiteSpace: "nowrap",
  opacity: 0,
  transition: "opacity 0.3s ease-in-out",
  pointerEvents: "none",
  backgroundColor: "rgba(42, 42, 42, .8)",
  zIndex: 1
});

/**
 * Component that displays Copy Code Button.
 * @returns A button containing the wpds link icon.
 */
export const CopyButton = ({ content, onBlur = () => {}, isMiniPlayer }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Clear copied message after 3 seconds
  useEffect(() => {
    if (showConfirmation === false) {
      return;
    }

    const timeoutId = setTimeout(() => setShowConfirmation(false), 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [showConfirmation]);

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      copyTextToClipboard(content, () => setShowConfirmation(true));
    },
    [content]
  );

  return (
    <CopyButtonRoot>
      <Button
        aria-label={showConfirmation ? "Link copied" : "Copy link"}
        icon="center"
        className={"focus-visible-highlight"}
        variant="primary"
        css={{
          border: "none",
          background: "none",
          "&:hover": {
            background: "none"
          },
          margin: "-$075",
          padding: "$075"
        }}
        onClick={handleClick}
        onBlur={onBlur}
      >
        <Icon fill="white" color="white" size={isMiniPlayer ? "100" : "150"}>
          {showConfirmation ? <Success /> : <Link />}
        </Icon>
      </Button>
      <StyledText
        role="status"
        aria-live="polite"
        aria-hidden={!showConfirmation}
        css={{
          ...(showConfirmation && { opacity: 1 })
        }}
      >
        Link copied
      </StyledText>
    </CopyButtonRoot>
  );
};

CopyButton.displayName = "CopyButton";

CopyButton.propTypes = {
  /** The content to copy to the clipboard. */
  content: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  isMiniPlayer: PropTypes.bool.isRequired
};
